import { useCallback, useEffect, useState } from "react";

import { Dialog } from "@ui/components/Dialog";
import { useDeviceBreakpoint } from "@ui/hooks/useDeviceBreakpoint";
import { useTranslations } from "@ui/providers/useTranslations";

import { RadioContainer } from "@jti/core-components";
import { Button } from "@jti/core-components/components/Button";

import styles from "./SelectItemDialog.module.scss";

type Item = {
  id: string;
};

export interface SelectItemDialogProps {
  <TItem extends Item>(props: {
    className?: string;
    isOpened: boolean;
    title: string;
    subTitle?: string;
    footerText?: string;
    btnSaveText?: string;
    btnCancelText?: string;
    items: TItem[];
    defaultSelectedId?: TItem["id"];
    renderItem(item: TItem): React.ReactNode;
    isPending?: boolean;
    createNew?: {
      label: string;
      onClick(): void;
    };
    onSave(id: TItem["id"]): void;
    onClose(): void;
    onCancel?(): void;
    portalDomNode?: HTMLElement;
  }): React.ReactElement;
}

export const SelectItemDialog: SelectItemDialogProps = ({
  className = "",
  isOpened,
  title,
  subTitle,
  footerText,
  items,
  defaultSelectedId = "",
  createNew,
  btnSaveText,
  btnCancelText,
  onClose,
  onSave,
  renderItem,
  isPending = false,
  portalDomNode,
}) => {
  const { __ } = useTranslations();
  const { breakpoint } = useDeviceBreakpoint();
  const [selectedItemId, setSelectedItemId] = useState<Item["id"]>(defaultSelectedId);

  const handleItemClick = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedItemId(event.currentTarget.value);
  }, []);

  useEffect(() => {
    if (isOpened) {
      setSelectedItemId(defaultSelectedId);
    }
  }, [isOpened, defaultSelectedId]);

  return (
    <Dialog isOpened={isOpened} onClose={onClose} className={className} portalDomNode={portalDomNode}>
      <div className={styles.dialog}>
        <div className={styles.title}>{title}</div>
        {subTitle && <div className={styles.subTitle}>{subTitle}</div>}
        <div className={styles.cardItems}>
          {items.map((item) => (
            <div className={`${styles.option} ${selectedItemId === item.id ? styles.selected : ""}`} key={item.id}>
              <RadioContainer
                value={item.id}
                checked={selectedItemId === item.id}
                onChange={handleItemClick}
                name="card-data"
                label={renderItem(item)}
              />
            </div>
          ))}
        </div>
        {footerText && <div className={styles.footerText}>{footerText}</div>}
        {createNew && (
          <div className={styles.createNew}>
            <Button
              data-testid="selectItemDialogCreateNewBtn"
              size={breakpoint === "mobile" ? undefined : "lg"}
              variant="text"
              onClick={createNew.onClick}
            >
              {createNew.label}
            </Button>
          </div>
        )}
        <div className={styles.btnGroup}>
          <Button
            data-testid="selectItemDialogCancelBtn"
            variant="secondary"
            size={breakpoint === "mobile" ? undefined : "lg"}
            onClick={onClose}
          >
            {btnCancelText || __("select_item_dialog.cancel")}
          </Button>
          <Button
            data-testid="selectItemDialogSaveBtn"
            variant="primary"
            onClick={() => onSave(selectedItemId)}
            pending={isPending}
            size={breakpoint === "mobile" ? undefined : "lg"}
            disabled={!selectedItemId && Boolean(items.length)}
          >
            {btnSaveText || __("select_item_dialog.confirm")}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};
